import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import FormHandler from "../../shared/utils/FormHandler";
import {
  getRoles,
  getReferencesForRole,
  generateCustomToken,
} from "../../utils/mainServices";
import { getUserId } from "../../shared/utils/SharedAuthentication";
import { toggleLoader } from "../../shared/actions/setting";
import { validateReferenceCodeModal } from "../../utils/validation";

const ReferenceCodeModal = ({ onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [roles, setRoles] = useState([]);
  const [references, setReferences] = useState([]);

  const [generatedCode, setGeneratedCode] = useState("");
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isLoadingReferences, setIsLoadingReferences] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formErr, setFormErr] = useState("");

  const triggerGenerate = () => {
    setIsLoading(true);
  };

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleOnBlur,
    setValue,
    initForm,
  } = FormHandler(triggerGenerate, validateReferenceCodeModal);

  // Initialize form
  useEffect(() => {
    initForm({
      roleId: "",
      referenceId: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load roles on mount
  useEffect(() => {
    const fetchRolesList = async () => {
      setIsLoadingRoles(true);
      dispatch(toggleLoader(true));
      try {
        const response = await getRoles();
        const roleList = response?.content || response || [];
        setRoles(Array.isArray(roleList) ? roleList : []);
      } catch (error) {
        console.error("Error fetching roles for reference code modal:", error);
        toast.error("Failed to load roles");
      } finally {
        setIsLoadingRoles(false);
        dispatch(toggleLoader(false));
      }
    };

    fetchRolesList();
  }, [dispatch]);

  // Fetch references when selected role changes
  useEffect(() => {
    if (!values.roleId) {
      setReferences([]);
      setValue({ referenceId: "" });
      return;
    }

    const fetchReferences = async () => {
      setIsLoadingReferences(true);
      dispatch(toggleLoader(true));
      try {
        const data = await getReferencesForRole(values.roleId);
        const refList = data?.content || data || [];
        setReferences(Array.isArray(refList) ? refList : []);
      } catch (err) {
        console.error("Error fetching references for role:", err);
        toast.error("Failed to fetch references for selected role.");
        setReferences([]);
      } finally {
        setIsLoadingReferences(false);
        dispatch(toggleLoader(false));
      }
    };

    fetchReferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.roleId, dispatch]);

  const handleRoleChange = (e) => {
    handleChange(e);
    setValue({ referenceId: "" });
    setGeneratedCode("");
    setFormErr("");
  };

  const handleReferenceChange = (e) => {
    handleChange(e);
    setGeneratedCode("");
    setFormErr("");
  };

  // Generate API execution via FormHandler pattern
  useEffect(() => {
    if (!isLoading) return;

    const generateCode = async () => {
      dispatch(toggleLoader(true));
      setFormErr("");

      const getter = getUserId();
      const payload = {
        roleId: Number(values.roleId) || values.roleId,
        referenceId: Number(values.referenceId) || values.referenceId,
        userRoleId: Number(values.roleId) || values.roleId,
        reference: Number(values.referenceId) || values.referenceId,
      };

      try {
        const response = await generateCustomToken(getter, payload);
        setGeneratedCode(response.content);
        toast.success("Reference code generated successfully!");
      } catch (error) {
        console.error("Error generating reference code:", error);
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to generate reference code";
        setFormErr(message);
        toast.error(message);
      } finally {
        setIsLoading(false);
        dispatch(toggleLoader(false));
      }
    };

    generateCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const resetForm = () => {
    initForm({});
    setGeneratedCode("");
    setFormErr("");
    onClose();
  };

  return (
    <div className="sa-popup-bg">
      <div className="sa-popup">
        <form onSubmit={handleSubmit} className="sa-modal-box-style">
          <div className="sa-popup-header">
            <span className="sa-model-heading">Generate Reference Code</span>
            <div className="sa-popup-close-icon" onClick={resetForm}>
              <FeatherIcon className="sa-modal-close-icon" icon="x" />
            </div>
          </div>

          <div className="sa-popup-content">
            <div className="col-md-6">
              <div className="form-group m-b-16">
                <label htmlFor="roleSelect">
                  Role <span className="text-red">*</span>
                </label>
                <select
                  id="roleSelect"
                  name="roleId"
                  className={`form-control ${errors.roleId ? "warning-input" : ""}`}
                  onBlur={handleOnBlur}
                  onChange={handleRoleChange}
                  value={values.roleId || ""}
                  disabled={isLoadingRoles || isLoading}
                >
                  <option value="">Select Role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {errors.roleId && (
                  <p className="warning-input-msg">{errors.roleId}</p>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group m-b-16">
                <label htmlFor="referenceSelect">
                  Reference <span className="text-red">*</span>
                </label>
                <select
                  id="referenceSelect"
                  name="referenceId"
                  className={`form-control ${errors.referenceId ? "warning-input" : ""}`}
                  onBlur={handleOnBlur}
                  onChange={handleReferenceChange}
                  value={values.referenceId || ""}
                  disabled={!values.roleId || isLoadingReferences || isLoading}
                >
                  <option value="">
                    {isLoadingReferences ? "Loading references..." : "Select Reference"}
                  </option>
                  {references.map((ref) => (
                    <option key={ref.id} value={ref.id}>
                      {ref.name}
                    </option>
                  ))}
                </select>
                {errors.referenceId && (
                  <p className="warning-input-msg">{errors.referenceId}</p>
                )}
              </div>
            </div>

            <div className="col-md-12">
              <div className="form-group m-b-16">
                <label htmlFor="generatedReferenceCode">Generated Reference Code</label>
                <input
                  type="text"
                  id="generatedReferenceCode"
                  name="generatedReferenceCode"
                  className="form-control view-text-muted"
                  value={generatedCode}
                  placeholder={isLoading ? "Generating..." : ""}
                  readOnly
                />
              </div>
            </div>
          </div>

          {formErr && <div className="login-warning-msg m-b-16">{formErr}</div>}

          <div className="sa-popup-btn">
            <button
              className="sa-popup-secondary-btn-style sa-popup-cancel-btn-style"
              type="button"
              onClick={resetForm}
            >
              {t("button.CANCEL", { defaultValue: "Cancel" })}
            </button>
            <button
              type="submit"
              className="sa-popup-secondary-btn-style"
              disabled={isLoading || !values.roleId || !values.referenceId}
            >
              {isLoading ? t("button.GENERATING", { defaultValue: "Generating..." }) : t("button.GENERATE", { defaultValue: "Generate" })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReferenceCodeModal;
